/**
 * Recent orders table — collapsed list of collected/administered orders with
 * settled payment, shown for context. Potentially reusable outside checkout.
 */
import { ChevronRight } from 'lucide-react'
import { useMemo } from 'react'

import type { OrderWithRelations } from '@snootsvet/shared'
import { formatClinicPrice, formatName } from '@snootsvet/shared'

import { FormattedDate } from '@/frontend/components/formatted-datetime'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/frontend/components/ui/collapsible'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import {
  AppointmentGroupCell,
  computeAppointmentRowSpans,
} from '@/frontend/features/orders/appointment-group'
import { OrderItemCell } from '@/frontend/features/orders/order-item-cell'

export interface RecentOrdersTableProps {
  orders: OrderWithRelations[]
}

export function RecentOrdersTable({ orders }: RecentOrdersTableProps) {
  const sorted = useMemo(
    () =>
      [...orders].sort((a, b) => {
        // Date descending
        const dateDiff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        if (dateDiff !== 0) return dateDiff
        // Group by consultation
        const aId = a.consultationId ?? ''
        const bId = b.consultationId ?? ''
        if (aId !== bId) return aId < bId ? -1 : 1
        return 0
      }),
    [orders]
  )
  const rowSpans = useMemo(() => computeAppointmentRowSpans(sorted), [sorted])

  if (orders.length === 0) return null

  return (
    <section className="rounded-lg border border-border p-3 shadow-xs">
      <Collapsible defaultOpen={false}>
        <CollapsibleTrigger className="group flex cursor-pointer items-center gap-1 font-medium text-muted-foreground">
          <ChevronRight className="size-4 transition-transform group-data-[panel-open]:rotate-90" />
          Recent Orders ({orders.length})
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <Table size="sm" variant="borderless" className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead className="w-6" />
                <TableHead className="w-40">Item</TableHead>
                <TableHead className="w-14 text-center">Qty</TableHead>
                <TableHead className="w-28">Prescriber</TableHead>
                <TableHead>Instructions</TableHead>
                <TableHead colSpan={2} className="w-64 text-center">
                  Dispensed
                </TableHead>
                <TableHead className="w-32 text-center">Batch</TableHead>
                <TableHead className="w-16 text-right">Discount</TableHead>
                <TableHead className="w-24 text-right">Price</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((order, idx) => {
                const batchNumbers = order.allocations
                  .filter((a) => !a.reversedAt && a.stock.batchNumber)
                  .map((a) => `${a.stock.batchNumber!} × ${a.quantity}`)
                  .join(', ')
                return (
                  <TableRow key={order.id} className="opacity-50">
                    <TableCell />
                    {rowSpans[idx] > 0 && (
                      <AppointmentGroupCell order={order} rowSpan={rowSpans[idx]} />
                    )}
                    <OrderItemCell order={order} />
                    <TableCell className="text-center">{order.quantity}</TableCell>
                    <TableCell
                      className="truncate text-muted-foreground"
                      title={order.treatment?.staff ? formatName(order.treatment.staff) : undefined}
                    >
                      {order.treatment?.staff ? formatName(order.treatment.staff) : '—'}
                    </TableCell>
                    <TableCell
                      className="truncate text-muted-foreground"
                      title={order.treatment?.instructions ?? undefined}
                    >
                      {order.treatment?.instructions ?? '—'}
                    </TableCell>
                    <TableCell colSpan={2} className="text-center">
                      {order.dispensedAt ? (
                        <FormattedDate value={order.dispensedAt} clinicId={order.clinicId} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="truncate text-center text-muted-foreground">
                      {batchNumbers || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {order.discountBps > 0 ? `${order.discountBps / 100}%` : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatClinicPrice(order.totalPrice, order.clinicId)}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CollapsibleContent>
      </Collapsible>
    </section>
  )
}
